import { createContext, useContext, useEffect, useState } from "react";
import api, { API } from "@/lib/api";

const GlobalConfigContext = createContext({
  config: null,
  refresh: () => {},
});

// Loads the super-admin-managed global config (styles, banner, support, FAQ,
// branding) and reflects style tokens + the default <title>/favicon onto the
// document. The useBrandMeta() hook on Dashboard overrides these when a brand
// is active and restores them on unmount.
const DEFAULTS = {
  styles: { radius: 8, action_color: "#1a1a1a", font_sans: "Inter", font_display: "Inter" },
  announcement: { text: "", active: false },
  support: { avatar_file_id: null, name: "", phone: "", email: "", show_phone: true, show_email: true },
  branding: { site_title: "The Brand Hub", favicon_file_id: null },
  faq: [],
};

function setFaviconHref(href) {
  const existing = document.querySelectorAll("link[rel~='icon']");
  existing.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
  const link = document.createElement("link");
  link.rel = "icon";
  link.href = href;
  document.head.appendChild(link);
}

export function GlobalConfigProvider({ children }) {
  const [config, setConfig] = useState(DEFAULTS);

  const refresh = async () => {
    try {
      const { data } = await api.get("/global-config");
      setConfig({
        ...DEFAULTS,
        ...data,
        styles: { ...DEFAULTS.styles, ...(data.styles || {}) },
        branding: { ...DEFAULTS.branding, ...(data.branding || {}) },
      });
    } catch { /* keep defaults */ }
  };

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    const s = config?.styles || DEFAULTS.styles;
    const r = document.documentElement.style;
    r.setProperty("--global-radius", `${s.radius}px`);
    r.setProperty("--global-radius-lg", `${Math.round(s.radius * 1.5)}px`);
    r.setProperty("--global-radius-xl", `${Math.round(s.radius * 2)}px`);
    r.setProperty("--global-action", s.action_color);
    r.setProperty("--font-sans", `"${s.font_sans}", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif`);
    r.setProperty("--font-display", `"${s.font_display}", -apple-system, BlinkMacSystemFont, sans-serif`);
  }, [config]);

  // Apply default title + favicon from the super-admin Branding tab. These
  // are the fallback on login / overview; useBrandMeta() takes over inside
  // an active brand hub and resets to these on unmount.
  useEffect(() => {
    const b = config?.branding || DEFAULTS.branding;
    const title = b.site_title || DEFAULTS.branding.site_title;
    const favicon = b.favicon_file_id ? `${API}/files/${b.favicon_file_id}` : "/favicon.svg";
    // Only touch defaults when no brand is actively driving the tab.
    if (!document.documentElement.dataset.brandActive) {
      document.title = title;
      setFaviconHref(favicon);
    }
    // Stash on root so useBrandMeta can read them for its fallback.
    document.documentElement.dataset.defaultTitle = title;
    document.documentElement.dataset.defaultFavicon = favicon;
  }, [config]);

  return (
    <GlobalConfigContext.Provider value={{ config, refresh }}>
      {children}
    </GlobalConfigContext.Provider>
  );
}

export const useGlobalConfig = () => useContext(GlobalConfigContext);
